import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Newspaper, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "./api";
import { Badge, btnPrimary, CATEGORY_LABELS, ConfirmButton, inputCls, Spinner, fmtDate } from "./ui";

const STATUS_FILTERS = [
    { id: "", label: "Όλα" },
    { id: "published", label: "Δημοσιευμένα" },
    { id: "draft", label: "Πρόχειρα" },
];

export const ArticlesPage = () => {
    const [articles, setArticles] = useState(null);
    const [status, setStatus] = useState("");
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");

    const load = useCallback(() => {
        const params = {};
        if (status) params.status = status;
        if (category) params.category = category;
        if (q.trim()) params.q = q.trim();
        api.get("/api/admin/articles", { params })
            .then((r) => setArticles(r.data))
            .catch((e) => {
                toast.error(apiError(e));
                setArticles([]);
            });
    }, [status, category, q]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const toggleStatus = async (a) => {
        const next = a.status === "published" ? "draft" : "published";
        try {
            await api.put(`/api/admin/articles/${a.id}`, { ...a, status: next });
            toast.success(next === "published" ? "Το άρθρο δημοσιεύτηκε." : "Το άρθρο επέστρεψε σε πρόχειρο.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const remove = async (a) => {
        try {
            await api.delete(`/api/admin/articles/${a.id}`);
            toast.success("Το άρθρο διαγράφηκε.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div className="mx-auto max-w-6xl" data-testid="admin-articles-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[#F2F5F6]">Νέα / Άρθρα</h1>
                    <p className="mt-1 text-sm text-[#697B80]">Δημιούργησε, επεξεργάσου και δημοσίευσε περιεχόμενο για τη σελίδα Νέα.</p>
                </div>
                <Link to="/articles/new" className={btnPrimary} data-testid="article-new-button">
                    <Plus size={16} /> Νέο άρθρο
                </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="flex gap-2" data-testid="article-status-filters">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setStatus(f.id)}
                            className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors duration-200 ${
                                status === f.id ? "border-[#22A6C0] bg-[#22A6C0]/15 text-[#4CC9DC]" : "border-white/10 text-[#A3B3B8] hover:border-white/25"
                            }`}
                            data-testid={`article-filter-${f.id || "all"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} w-auto`} data-testid="article-category-filter">
                    <option value="">Όλες οι κατηγορίες</option>
                    {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                    ))}
                </select>
                <div className="relative min-w-[200px] flex-1">
                    <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#697B80]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Αναζήτηση τίτλου ή slug…"
                        className={`${inputCls} pl-10`}
                        data-testid="article-search"
                    />
                </div>
            </div>

            {articles === null ? (
                <Spinner testId="articles-loading" />
            ) : articles.length === 0 ? (
                <p className="mt-16 text-center text-sm text-[#697B80]" data-testid="articles-empty">Δεν βρέθηκαν άρθρα.</p>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="articles-list">
                    {articles.map((a) => (
                        <li key={a.id} className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#093448] p-4 sm:flex-row sm:items-center" data-testid={`article-row-${a.id}`}>
                            <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#052536]">
                                {a.featured_image ? (
                                    <img src={a.featured_image} alt="" className="h-full w-full object-cover" />
                                ) : a.type === "instagram" ? (
                                    <Instagram size={20} className="text-[#697B80]" />
                                ) : (
                                    <Newspaper size={20} className="text-[#697B80]" />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <Link to={`/articles/${a.id}`} className="block truncate text-sm font-bold text-[#F2F5F6] hover:text-[#4CC9DC]">
                                    {a.title_el}
                                </Link>
                                <p className="mt-0.5 truncate text-xs text-[#697B80]">/nea/{a.slug} · {fmtDate(a.published_at)}</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Badge className="border-white/15 text-[#A3B3B8]">{CATEGORY_LABELS[a.category] || a.category}</Badge>
                                    <Badge className={a.status === "published" ? "border-[#5FBBAC]/40 bg-[#5FBBAC]/10 text-[#5FBBAC]" : "border-[#F7EAB8]/40 bg-[#F7EAB8]/10 text-[#F7EAB8]"}>
                                        {a.status === "published" ? "Δημοσιευμένο" : "Πρόχειρο"}
                                    </Badge>
                                </div>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center gap-2">
                                <button onClick={() => toggleStatus(a)} className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-[#A3B3B8] transition-colors hover:border-[#4CC9DC] hover:text-[#F2F5F6]" data-testid={`article-toggle-${a.id}`}>
                                    {a.status === "published" ? "Απόσυρση" : "Δημοσίευση"}
                                </button>
                                <Link to={`/articles/${a.id}`} className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-[#A3B3B8] transition-colors hover:border-[#4CC9DC] hover:text-[#F2F5F6]" data-testid={`article-edit-${a.id}`}>
                                    Επεξεργασία
                                </Link>
                                <ConfirmButton onConfirm={() => remove(a)} label="Διαγραφή" testId={`article-delete-${a.id}`} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
